import { getCommunicationsPage } from "@/api/client/communications";
import { fetchCommunicationBatches } from "@/lib/communication-listing";

import { createQuestionChartAccumulator } from "./question-option-charts";
import {
  countAnonymity,
  createStatsSummaryAccumulator,
} from "./stats-summary-table";

export const STATS_BATCH_SIZE = 100;

export const resolveAnswerQuestion = (answer, questionById) => {
  if (!answer || typeof answer !== "object") {
    return null;
  }
  if (answer.question && typeof answer.question === "object") {
    return answer.question;
  }
  const rawId =
    answer.questionId ??
    answer.question_id ??
    (typeof answer.question === "number" || typeof answer.question === "string"
      ? answer.question
      : null);
  if (rawId === null || rawId === undefined || rawId === "") {
    return null;
  }
  if (!questionById || typeof questionById.get !== "function") {
    return null;
  }
  return (
    questionById.get(rawId) ??
    questionById.get(Number(rawId)) ??
    questionById.get(String(rawId)) ??
    null
  );
};

const collectCatalogById = (questionsByType) => {
  const byId = new Map();
  const lists = [
    questionsByType?.complaint ?? questionsByType?.complaints ?? [],
    questionsByType?.consultation ??
      questionsByType?.consultations ??
      questionsByType?.inquiry ??
      questionsByType?.inquiries ??
      [],
  ];
  for (const list of lists) {
    if (!Array.isArray(list)) {
      continue;
    }
    for (const question of list) {
      if (question && typeof question === "object" && question.id !== null && question.id !== undefined) {
        byId.set(question.id, question);
      }
    }
  }
  return byId;
};

const withResolvedQuestions = (rows, questionById) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    if (!row || typeof row !== "object") {
      return row;
    }
    const answers = Array.isArray(row.answers) ? row.answers : [];
    return {
      ...row,
      answers: answers.map((answer) =>
        answer && typeof answer === "object" && !answer.question
          ? { ...answer, question: resolveAnswerQuestion(answer, questionById) }
          : answer,
      ),
    };
  });

/**
 * Acumulador incremental para la página de estadísticas: alimenta lotes
 * acotados de comunicaciones (compactas) y libera cada lote tras contar,
 * sin reconstruir el universo completo en el navegador.
 */
export const createStatsAccumulator = ({
  questionsByType,
  language = "es",
  typeLabels = {},
} = {}) => {
  const questionById = collectCatalogById(questionsByType);
  const charts = createQuestionChartAccumulator({
    questionsByType,
    language,
    typeLabels,
  });
  const summary = createStatsSummaryAccumulator({
    questionsByType,
    language,
  });
  const anonymous = {
    complaint: { anonymous: 0, nonAnonymous: 0 },
    consultation: { anonymous: 0, nonAnonymous: 0 },
  };

  return {
    feed(rows, communicationType) {
      const key =
        communicationType === "consultation" ? "consultation" : "complaint";
      const resolved = withResolvedQuestions(rows, questionById);
      charts.feed(resolved, communicationType);
      summary.feed(resolved, communicationType);
      const counts = countAnonymity(resolved);
      anonymous[key].anonymous += counts.anonymous;
      anonymous[key].nonAnonymous += counts.nominative;
    },
    buildCharts() {
      return charts.build();
    },
    build(t) {
      return {
        questionOptionCharts: charts.build(),
        summary: summary.build(),
        anonymousPies: this.buildAnonymousPies(t),
      };
    },
    buildSummary() {
      return summary.build();
    },
    buildAnonymousPies(t) {
      const translate = typeof t === "function" ? t : (key) => key;
      const buildPie = (counts) =>
        [
          counts.anonymous > 0
            ? {
                name: translate("statsPage.graph3.anonymous"),
                value: counts.anonymous,
              }
            : null,
          counts.nonAnonymous > 0
            ? {
                name: translate("statsPage.graph3.nonAnonymous"),
                value: counts.nonAnonymous,
              }
            : null,
        ].filter(Boolean);
      return {
        complaint: buildPie(anonymous.complaint),
        consultation: buildPie(anonymous.consultation),
      };
    },
  };
};

export const loadStatsUniverseBatched = async ({
  fromDate,
  toDate,
  filterByDate,
  questionsByType,
  language,
  typeLabels,
  t,
  batchSize = STATS_BATCH_SIZE,
  onProgress,
  fetchPage = getCommunicationsPage,
} = {}) => {
  const accumulator = createStatsAccumulator({
    questionsByType,
    language,
    typeLabels,
  });
  const totals = { complaint: 0, consultation: 0 };

  for (const type of ["complaint", "consultation"]) {
    await fetchCommunicationBatches(fetchPage, {
        type,
        batchSize,
        onBatch: async (rows, pagination) => {
          totals[type] = pagination?.total ?? totals[type];
          const filtered =
            typeof filterByDate === "function"
              ? filterByDate(rows, fromDate, toDate)
              : rows;
          accumulator.feed(filtered, type);
          if (typeof onProgress === "function") {
            await onProgress({ type, rows, pagination });
          }
        },
      },
    );
  }

  return {
    questionOptionCharts: accumulator.buildCharts(),
    summary: accumulator.buildSummary(),
    anonymousPies: accumulator.buildAnonymousPies(t),
    totals,
  };
};
